import { queryOptions, type QueryClient } from '@tanstack/react-query'
import { ApiError } from './api/client'
import { fetchSession } from './api/session'
import type { CurrentUser } from './api/types'

/**
 * The signed-in identity, held in the query cache rather than in a context of
 * its own.
 *
 * One source of truth: a permission change on the server reaches the UI on the
 * next refetch, and every screen sees it at once. Nothing about the user is
 * persisted to localStorage - the session cookie is the only thing that
 * survives a reload, so the browser can never believe it is signed in when the
 * server disagrees.
 */
export const sessionQueryOptions = queryOptions({
  queryKey: ['session'] as const,
  queryFn: ({ signal }) => fetchSession(signal),
  // A 401 is the normal answer for a signed-out visitor, not a transient
  // failure, so retrying it only delays the redirect to the login screen.
  retry: (failureCount, error) =>
    !(error instanceof ApiError && error.isUnauthorized) && failureCount < 2,
  staleTime: 30_000,
})

/**
 * Resolves the current user for a route guard, treating "not signed in" as a
 * value rather than as an error.
 */
export async function loadSession(queryClient: QueryClient): Promise<CurrentUser | null> {
  try {
    return await queryClient.ensureQueryData(sessionQueryOptions)
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) return null
    throw error
  }
}
